<!--
  A group's settings, in tabs: People (search, bulk add, colors, archive; built for groups from
  a handful of people to hundreds), Stores, Lists and Import.
-->
<script lang="ts">
  import * as H from "../settings/hints.ts";
  import { fill } from "../settings/index.ts";
  import { MCC_CODES, PALETTE, formatBp, guessMcc, mccInfo, planSheetImport, exportGroups, isExportFile, planCsvImport, planStateImport, memberAccessOf, treasurerAccessOf, memberAccessFor, treasurerAccessFor, hiddenCategoriesFor, joiningOf, whyNotYou, type Holder, type PersonAccess, type ImportPlan, type SheetSnapshot, type GroupState as GS } from "@split/core";
  import Swatch from "../components/Swatch.svelte";
  import { app } from "../lib/store.svelte.ts";
  import { link } from "../lib/router.svelte.ts";
  import { readPercent, tidyName } from "../lib/format.ts";
  import { api } from "../lib/api.ts";

  const S = $derived(app.current);
  /** Pages a role can be given, besides the Ledger (everyone has it). */
  const ROLE_PAGES = [
    ["people", "People", "everyone and what each owes"],
    ["receipts", "Receipts", "receipt totals and the catalog"],
    ["insights", "Overview", "the group's dashboard"],
    ["history", "History", "every change, with undo"],
    ["settings", "Settings", "people, stores, lists, who sees what"],
  ] as const;
  const people = $derived(S.personOrder.map((id) => S.people[id]!).filter(Boolean));

  // ---- who sees what, just for one person (on top of their role's column)
  let paFor = $state("");
  /** Club treasurers in this group, so their column shows History and Settings too. */
  let treasurers = $state<Set<string>>(new Set());
  /** Whose account holds each person here, for "Which one is you" (packages/core whoIsYou). */
  let holders = $state<Map<string, Holder>>(new Map());
  $effect(() => {
    const gid = app.groupId;
    if (!gid) return;
    api<{ members: { personId: string; role: string; holder?: Holder }[] }>(`/api/groups/${gid}/members`)
      .then((r) => {
        treasurers = new Set(r.members.filter((m) => m.role === "treasurer").map((m) => m.personId));
        holders = new Map(r.members.filter((m) => m.holder).map((m) => [m.personId, m.holder!]));
      })
      .catch(() => {});
  });
  /** Why the current "Which one is you" is wrong (someone else's own account), or "". */
  const youProblem = $derived(whyNotYou(S, S.settings.ownerPersonId, (id) => holders.get(id)) ?? "");
  const paPeople = $derived(people.filter((p) => !p.archived && p.id !== S.settings.ownerPersonId));
  const paName = $derived(S.people[paFor]?.name ?? "");
  const paTreasurer = $derived(treasurers.has(paFor));
  /** What the chosen person gets now: their role's column, then their own changes. */
  const paPages = $derived(
    paTreasurer
      ? treasurerAccessFor(S.settings, paFor)
      : { ...memberAccessFor(S.settings, paFor), history: false, settings: false },
  );
  const paOwn = $derived(S.settings.personAccess?.[paFor] ?? null);
  /** Every category items can land in (lists, store defaults, and Other). */
  const allCategories = $derived(
    [...new Set([...S.settings.categories, ...Object.values(S.stores).map((st) => st.category), "Other"])].sort((a, b) =>
      a === "Other" ? 1 : b === "Other" ? -1 : a.localeCompare(b),
    ),
  );
  const paHidden = $derived(new Set(hiddenCategoriesFor(S.settings, paFor)));

  function savePersonAccess(next: PersonAccess | null) {
    const all = { ...(S.settings.personAccess ?? {}) };
    const empty = !next || (!Object.keys(next.pages ?? {}).length && !next.hiddenCategories?.length);
    if (empty) delete all[paFor];
    else all[paFor] = next!;
    return app.do_({ type: "settings.update", patch: { personAccess: all } }, empty ? `${paName} follows the group's setting again` : "Saved");
  }
  function setPersonPage(key: (typeof ROLE_PAGES)[number][0], on: boolean) {
    const role = paTreasurer ? treasurerAccessOf(S.settings)[key] : (memberAccessOf(S.settings) as Record<string, boolean>)[key];
    const pages = { ...(paOwn?.pages ?? {}) };
    if (on === role) delete pages[key];
    else pages[key] = on;
    return savePersonAccess({ ...(paOwn ?? {}), pages });
  }
  function setPersonCategory(cat: string, shown: boolean) {
    const hidden = new Set(paOwn?.hiddenCategories ?? []);
    if (shown) hidden.delete(cat);
    else hidden.add(cat);
    return savePersonAccess({ ...(paOwn ?? {}), hiddenCategories: [...hidden].sort() });
  }
  const groupName = $derived(app.groups.find((g) => g.id === app.groupId)?.name ?? S.name);
  const stores = $derived(Object.values(S.stores).sort((a, b) => a.name.localeCompare(b.name)));

  type Tab = "people" | "stores" | "lists" | "import";
  // A link like …/settings?tab=lists opens straight on that tab.
  const asked = typeof location === "undefined" ? null : new URLSearchParams(location.search).get("tab");
  let tab = $state<Tab>(asked === "stores" || asked === "lists" || asked === "import" ? asked : "people");

  // ---- people
  let q = $state("");
  let show = $state<"active" | "archived" | "all">("active");
  const shown = $derived.by(() => {
    const needle = q.trim().toLowerCase();
    return people.filter(
      (p) =>
        (show === "all" || (show === "archived" ? p.archived : !p.archived)) &&
        (!needle || p.name.toLowerCase().includes(needle)),
    );
  });
  const activeCount = $derived(people.filter((p) => !p.archived).length);
  const PAGE = 60;
  let limit = $state(PAGE);
  $effect(() => {
    void q;
    void show;
    limit = PAGE;
  });

  async function rename(id: string, value: string) {
    const name = tidyName(value);
    if (name && name !== S.people[id]?.name) await app.do_({ type: "person.update", personId: id, patch: { name } }, "Renamed");
  }

  // Bulk add: paste names, one per line or separated by commas.
  let bulk = $state("");
  let adding = $state(0);
  const plannedPeople = $derived.by(() => {
    const seen = new Set(people.map((p) => p.name.toLowerCase()));
    const out: { name: string; skip: string | null }[] = [];
    for (const raw of bulk.split(/[\n,;]+/)) {
      const name = tidyName(raw);
      if (!name) continue;
      const key = name.toLowerCase();
      if (seen.has(key)) {
        out.push({ name, skip: people.some((p) => p.name.toLowerCase() === key) ? "already in the group" : "listed twice" });
        continue;
      }
      seen.add(key);
      out.push({ name, skip: name.split(" ").length < 2 ? "needs a last name" : null });
    }
    return out;
  });
  const toAdd = $derived(plannedPeople.filter((p) => !p.skip));

  /** The least-used colors first, so a big group spreads across the palette. */
  function nextColors(n: number): string[] {
    const use = new Map<string, number>(PALETTE.map((c) => [c, 0]));
    for (const p of people) use.set(p.color, (use.get(p.color) ?? 0) + 1);
    const out: string[] = [];
    for (let i = 0; i < n; i++) {
      const [c] = [...use.entries()].sort((a, b) => a[1] - b[1] || PALETTE.indexOf(a[0] as never) - PALETTE.indexOf(b[0] as never))[0]!;
      out.push(c);
      use.set(c, use.get(c)! + 1);
    }
    return out;
  }

  async function addAll() {
    const list = toAdd;
    const colors = nextColors(list.length);
    adding = list.length;
    let n = 0;
    for (const [i, p] of list.entries()) {
      const err = await app.do_({ type: "person.add", personId: crypto.randomUUID(), name: p.name, color: colors[i]! });
      if (err) {
        app.toast(`Stopped at ${p.name}: ${err}`, "error");
        break;
      }
      n++;
      adding = list.length - n;
    }
    adding = 0;
    if (n) {
      app.toast(n === 1 ? `Added ${list[0]!.name}` : `Added ${n} people`);
      bulk = "";
    }
  }

  // ---- store presets
  let storeName = $state("");
  let storeTax = $state("");
  let storeTip = $state("");
  let storeCat = $state("");
  let storeMcc = $state("");
  async function addStore(e: SubmitEvent) {
    e.preventDefault();
    const bp = readPercent(storeTax || "0");
    const tipBp = readPercent(storeTip || "0");
    if (bp === null) return app.toast("Enter tax like 7.75", "error");
    if (tipBp === null) return app.toast("Enter tip like 18", "error");
    const name = storeName.trim() === storeName.trim().toLowerCase() ? tidyName(storeName) : storeName.trim();
    const mcc = storeMcc.trim() || guessMcc(name)?.code || undefined;
    const err = await app.do_({ type: "store.set", name, taxBp: bp, tipBp, mcc, category: storeCat.trim() || mccInfo(mcc)?.category || "Other" }, "Preset saved");
    if (!err) storeName = storeTax = storeTip = storeCat = storeMcc = "";
  }

  function editStore(st: { name: string; taxBp: number; tipBp?: number; mcc?: string; category: string }) {
    storeMcc = st.mcc ?? "";
    storeName = st.name;
    storeTax = String(st.taxBp / 100);
    storeTip = st.tipBp ? String(st.tipBp / 100) : "";
    storeCat = st.category;
  }

  // ---- lists
  async function saveList(key: "categories" | "cards", text: string) {
    const list = text
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    await app.do_({ type: "settings.update", patch: { [key]: list } }, "Saved");
  }

  // ---- import
  let plan = $state<ImportPlan | null>(null);
  let planName = $state("");
  let planKind = $state("");
  /** A Split Ledger download can hold several groups: you pick which to bring in. */
  let exportChoices = $state<{ id: string; name: string; state: GS }[]>([]);
  async function pickFile(e: Event) {
    const input = e.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;
    plan = null;
    exportChoices = [];
    planName = file.name;
    const text = await file.text();
    if (/\.csv$/i.test(file.name) || (!text.trimStart().startsWith("{") && text.includes(","))) {
      plan = planCsvImport(text, S, file.name);
      planKind = "CSV spreadsheet";
      return;
    }
    let json: unknown;
    try {
      json = JSON.parse(text);
    } catch {
      app.toast("That file isn't JSON or CSV.", "error");
      return;
    }
    if (isExportFile(json)) {
      exportChoices = exportGroups(json);
      planKind = "Split Ledger download";
      if (exportChoices.length === 1) choose(exportChoices[0]!);
      return;
    }
    const snap = json as SheetSnapshot;
    if (snap && typeof snap === "object" && "receipt" in snap && "settings" in snap) {
      plan = planSheetImport(snap);
      planKind = "Google Sheet snapshot";
      return;
    }
    app.toast("That JSON isn't a Google Sheet snapshot or a Split Ledger download.", "error");
  }
  function choose(g: { name: string; state: GS }) {
    plan = planStateImport(g.state, `${planName} ${g.name}`);
    planName = `${g.name} (from ${planName})`;
  }
  async function runImport() {
    if (!plan) return;
    const n = plan.commands.length;
    if (await app.importCommands($state.snapshot(plan.commands))) {
      app.toast(`Importing ${n} changes. They'll sync in batches of 50.`);
      plan = null;
    }
  }

  const TABS: [Tab, string][] = [
    ["people", "People"],
    ["stores", "Stores"],
    ["lists", "Categories and cards"],
    ["import", "Import"],
  ];
</script>

<div class="page">
  <div class="page-head">
    <div>
      <h1>Group settings</h1>
      <p class="lede">For {groupName}. Your account and devices are in <a href="/you" use:link>Your settings</a>.</p>
    </div>
  </div>

  <div class="tabs" role="tablist">
    {#each TABS as [t, label]}
      <button role="tab" aria-selected={tab === t} onclick={() => (tab = t)}>
        {label}{#if t === "people"}<span class="count">{activeCount}</span>{:else if t === "stores"}<span class="count">{stores.length}</span>{/if}
      </button>
    {/each}
  </div>

  {#if tab === "people"}
    <section class="sec">
      <div class="you">
        <label class="field">
          <span>Which one is you</span>
          <select class="input" value={S.settings.ownerPersonId ?? ""} onchange={(e) => app.do_({ type: "settings.update", patch: { ownerPersonId: (e.currentTarget as HTMLSelectElement).value || null } }, "Saved")}>
            <option value="">Not set</option>
            {#each people.filter((p) => !p.archived || p.id === S.settings.ownerPersonId) as p (p.id)}
              {@const own = holders.get(p.id) === "member"}
              <option value={p.id} disabled={own && p.id !== S.settings.ownerPersonId}>{p.name}{own ? H.GS_YOU_HAS_ACCOUNT : ""}</option>
            {/each}
          </select>
        </label>
        {#if H.GS_YOU_NOTE.trim()}<p class="note muted">{H.GS_YOU_NOTE}</p>{/if}
        {#if youProblem && H.GS_YOU_IS_SOMEONE_ELSE.trim()}
          <p class="note warn-you" role="alert">{fill(H.GS_YOU_IS_SOMEONE_ELSE, { name: S.people[S.settings.ownerPersonId ?? ""]?.name ?? "" })}</p>
        {/if}
      </div>
    </section>

    <section class="sec">
      <h2>Who sees what</h2>
      {#if H.GS_ACCESS_NOTE.trim()}<p class="note muted">{H.GS_ACCESS_NOTE}</p>{/if}
      <label class="field person-pick">
        <span>Just for one person</span>
        <select class="input" bind:value={paFor} aria-label="Just for one person">
          <option value="">Everyone in their role</option>
          {#each paPeople as p (p.id)}<option value={p.id}>{p.name}</option>{/each}
        </select>
      </label>
      <div class="roles" class:with-person={!!paFor} role="table" aria-label="Who sees what">
        <div class="rrow head" role="row">
          <span role="columnheader">Page</span>
          <span role="columnheader">Members</span>
          <span role="columnheader">Club treasurer</span>
          {#if paFor}<span role="columnheader">{paName}</span>{/if}
        </div>
        {#each ROLE_PAGES as [key, label, hint] (key)}
          <div class="rrow" role="row">
            <span role="cell"><strong>{label}</strong> <span class="muted small">{hint}</span></span>
            <span role="cell">
              {#if key in memberAccessOf(S.settings)}
                <input type="checkbox" aria-label="Members see {label}" checked={memberAccessOf(S.settings)[key as "people"]} onchange={(e) => app.do_({ type: "settings.update", patch: { memberAccess: { ...memberAccessOf(S.settings), [key]: (e.currentTarget as HTMLInputElement).checked } } }, "Saved")} />
              {:else}
                <span class="muted small">No</span>
              {/if}
            </span>
            <span role="cell">
              <input type="checkbox" aria-label="Club treasurers see {label}" checked={treasurerAccessOf(S.settings)[key]} onchange={(e) => app.do_({ type: "settings.update", patch: { treasurerAccess: { ...treasurerAccessOf(S.settings), [key]: (e.currentTarget as HTMLInputElement).checked } } }, "Saved")} />
            </span>
            {#if paFor}
              <span role="cell" class="own">
                {#if paTreasurer || key in memberAccessOf(S.settings)}
                  <input type="checkbox" aria-label="{paName} sees {label}" checked={paPages[key]} onchange={(e) => setPersonPage(key, (e.currentTarget as HTMLInputElement).checked)} />
                  {#if paOwn?.pages && key in paOwn.pages}<span class="muted tiny">just them</span>{/if}
                {:else}
                  <span class="muted small">No</span>
                {/if}
              </span>
            {/if}
          </div>
        {/each}
      </div>
      {#if paFor}
        <div class="cats">
          <p class="cats-h"><strong>Categories {paName} sees</strong></p>
          <div class="cat-list">
            {#each allCategories as c (c)}
              <label class="toggle-row"><input type="checkbox" checked={!paHidden.has(c)} onchange={(e) => setPersonCategory(c, (e.currentTarget as HTMLInputElement).checked)} />{c}</label>
            {/each}
          </div>
          <button class="btn small quiet" disabled={!paOwn} onclick={() => savePersonAccess(null)}>Use the group's setting for {paName}</button>
        </div>
      {/if}
    </section>

    <section class="sec">
      <h2>Who can join</h2>
      {#if H.GS_JOINING_NOTE.trim()}<p class="note muted">{H.GS_JOINING_NOTE}</p>{/if}
      {#if joiningOf({}, groupName) === "closed"}
        {#if H.GS_TESTS_NOTE.trim()}<p class="note muted">{H.GS_TESTS_NOTE}</p>{/if}
      {:else}
        {#each [["open", "Anyone who signs up", "They join right away as a new person and see the group"], ["ask", "Ask me first", "You confirm each one under Join requests"], ["closed", "Invite links only", "This group isn't listed on sign-up"]] as const as [value, label, hint] (value)}
          <label class="toggle-row">
            <input type="radio" name="joining" checked={joiningOf(S.settings, groupName) === value} onchange={() => app.do_({ type: "settings.update", patch: { joining: value } }, "Saved")} />
            <span><strong>{label}</strong> <span class="muted">{hint}</span></span>
          </label>
        {/each}
      {/if}
    </section>

    <section class="sec">
      <h2>Add people</h2>
      <label class="field">
        <span>Names, one per line or separated by commas</span>
        <textarea class="input" rows="3" bind:value={bulk} placeholder={"Emily Chen\nChloe Park, Alex Kim"}></textarea>
      </label>
      {#if plannedPeople.length}
        <div class="preview">
          {#each plannedPeople as p}
            <span class="chip" class:skip={p.skip} title={p.skip ?? ""}>{p.name}{#if p.skip}<span class="why">{p.skip}</span>{/if}</span>
          {/each}
        </div>
      {/if}
      <div class="inline">
        <button class="btn primary" disabled={!toAdd.length || adding > 0} onclick={addAll}>
          {adding ? `Adding… ${adding} left` : toAdd.length > 1 ? `Add ${toAdd.length} people` : "Add person"}
        </button>
        {#if H.GS_COLORS_NOTE.trim()}<span class="muted small">{H.GS_COLORS_NOTE}</span>{/if}
      </div>
    </section>

    <section class="sec">
      <div class="tools">
        <input class="input" type="search" bind:value={q} placeholder="Search people" aria-label="Search people" />
        <div class="seg" role="group" aria-label="Show">
          <button aria-pressed={show === "active"} onclick={() => (show = "active")}>Active</button>
          <button aria-pressed={show === "archived"} onclick={() => (show = "archived")}>Archived</button>
          <button aria-pressed={show === "all"} onclick={() => (show = "all")}>All</button>
        </div>
      </div>
      <div class="list">
        {#each shown.slice(0, limit) as p (p.id)}
          <div class="prow" class:archived={p.archived}>
            <Swatch color={p.color} status="paid" size={22} />
            <input class="input" value={p.name} aria-label="Name" onchange={(e) => rename(p.id, (e.currentTarget as HTMLInputElement).value)} />
            <select class="input" value={p.color} aria-label="Color for {p.name}" onchange={(e) => app.do_({ type: "person.update", personId: p.id, patch: { color: (e.currentTarget as HTMLSelectElement).value } })}>
              {#each PALETTE as c}<option value={c}>{c}</option>{/each}
            </select>
            <button class="btn small quiet" onclick={() => app.do_({ type: "person.update", personId: p.id, patch: { archived: !p.archived } }, p.archived ? "Restored" : "Archived")}>
              {p.archived ? "Restore" : "Archive"}
            </button>
          </div>
        {:else}
          <div class="empty">{q ? "Nobody matches." : show === "archived" ? "Nobody is archived." : "No people yet."}</div>
        {/each}
      </div>
      {#if shown.length > limit}
        <button class="btn small" onclick={() => (limit += PAGE)}>Show {Math.min(PAGE, shown.length - limit)} more of {shown.length - limit}</button>
      {/if}
      {#if H.GS_PEOPLE_NOTE.trim()}<p class="note muted">{H.GS_PEOPLE_NOTE}</p>{/if}
    </section>
  {:else if tab === "stores"}
    <section class="sec">
      {#if H.GS_STORES_NOTE.trim()}<p class="note muted">{H.GS_STORES_NOTE}</p>{/if}
      <div class="list">
        {#each stores as s (s.name)}
          <div class="srow">
            <button class="link-btn" onclick={() => editStore(s)} title="Edit"><strong>{s.name}</strong></button>
            <span class="num">{formatBp(s.taxBp)}{s.tipBp ? ` + ${formatBp(s.tipBp)} tip` : ""}</span>
            <span class="muted">{s.category}{#if s.mcc}<span class="mcc" title="Merchant code {s.mcc}"> · {mccInfo(s.mcc)?.label ?? `MCC ${s.mcc}`}</span>{/if}</span>
            <button class="btn small quiet danger" onclick={() => app.do_({ type: "store.remove", name: s.name }, "Preset removed")}>Remove</button>
          </div>
        {:else}
          <div class="empty">No presets yet.</div>
        {/each}
      </div>
      <form class="inline" onsubmit={addStore}>
        <label class="field grow"><span>Store</span><input class="input" bind:value={storeName} placeholder="Trader Joe's" /></label>
        <label class="field"><span>Tax %</span><input class="input money" inputmode="decimal" bind:value={storeTax} placeholder="7.75" /></label>
        <label class="field"><span>Tip %</span><input class="input money" inputmode="decimal" bind:value={storeTip} placeholder="0" /></label>
        <label class="field"><span>Category</span><input class="input" bind:value={storeCat} list="cats" placeholder="Groceries" /></label>
        <label class="field"
          ><span>Merchant type</span>
          <select class="input" bind:value={storeMcc}>
            <option value="">{guessMcc(storeName)?.label ? `Guess: ${guessMcc(storeName)?.label}` : "Not set"}</option>
            {#each MCC_CODES as m (m.code)}<option value={m.code}>{m.label} ({m.code})</option>{/each}
          </select></label
        >
        <datalist id="cats">{#each S.settings.categories as c}<option value={c}></option>{/each}</datalist>
        <button class="btn" disabled={!storeName.trim()}>Save preset</button>
      </form>
    </section>
  {:else if tab === "lists"}
    <section class="sec two">
      <label class="field">
        <span>Categories, one per line</span>
        <textarea class="input" rows="8" value={S.settings.categories.join("\n")} onchange={(e) => saveList("categories", (e.currentTarget as HTMLTextAreaElement).value)}></textarea>
      </label>
      <div class="field">
        <label class="field">
          <span>Other ways to pay in this group, one per line</span>
          <textarea class="input" rows="8" value={S.settings.cards.join("\n")} onchange={(e) => saveList("cards", (e.currentTarget as HTMLTextAreaElement).value)}></textarea>
        </label>
        {#if H.GS_CARDS_NOTE.trim()}<p class="note muted">{H.GS_CARDS_NOTE}</p>{/if}
      </div>
    </section>
  {:else}
    <section class="sec">
      <h2>Import</h2>
      {#if H.GS_IMPORT_NOTE.trim()}<p class="note muted">{H.GS_IMPORT_NOTE}</p>{/if}
      <ul class="formats">
        <li><strong>Google Sheet</strong> (the old Costco Sheet): a <span class="code">.json</span> snapshot made by <span class="code">scripts/import_sheet.py</span>.</li>
        <li><strong>Split Ledger download</strong>: the <span class="code">.json</span> from Your settings → Download your data. You choose which group to bring in.</li>
        <li><strong>CSV spreadsheet</strong> (<span class="code">.csv</span>, from Excel, Numbers or Google Sheets → Download → CSV): a header row with columns like <em>Date, Store, Description, Price, Split between</em>. Optional: Item #, Category, Coupon, CRV, Tax %, Tip %, Card, Paid by, Receipt, Note. People are separated by commas or semicolons; new names are added.</li>
      </ul>
      <label class="btn">Choose a file<input type="file" accept="application/json,.json,text/csv,.csv" onchange={pickFile} hidden /></label>
      {#if exportChoices.length > 1 && !plan}
        <div class="plan">
          <p><strong>{planName}</strong> has {exportChoices.length} groups. Which one?</p>
          <div class="inline">
            {#each exportChoices as g (g.id)}
              <button class="btn small" onclick={() => choose(g)}>{g.name} ({Object.keys(g.state.items).length} items)</button>
            {/each}
          </div>
        </div>
      {/if}
      {#if plan}
        <div class="plan">
          <p>
            <span class="muted">{planKind}:</span> <strong>{planName}</strong>: {plan.commands.filter((c) => c.cmd.type === "item.add").length} items,
            {plan.commands.filter((c) => c.cmd.type === "payment.add").length} payments,
            {plan.commands.filter((c) => c.cmd.type === "person.add").length} new people.
          </p>
          {#if plan.problems.length}
            <ul class="owes">{#each plan.problems as p}<li>{p}</li>{/each}</ul>
          {/if}
          <button class="btn primary" onclick={runImport}>Import {plan.commands.length} changes</button>
        </div>
      {/if}
    </section>
  {/if}
</div>

<style>
  .toggle-row {
    display: flex;
    gap: 10px;
    align-items: baseline;
    padding: 6px 0;
  }

  .tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 2px;
    padding: 3px;
    border-radius: 12px;
    background: var(--wash);
    width: fit-content;
    max-width: 100%;
  }

  .tabs button {
    padding: 8px 14px;
    border: 0;
    border-radius: 9px;
    background: none;
    color: var(--ink-2);
    font: inherit;
    font-weight: 600;
    font-size: var(--t-sm);
    cursor: pointer;
  }

  .tabs button[aria-selected="true"] {
    background: var(--surface);
    color: var(--ink);
    box-shadow: 0 1px 2px rgb(0 0 0 / 0.12);
  }

  .count {
    margin-left: 6px;
    color: var(--muted);
    font-weight: 500;
  }

  .sec {
    display: grid;
    gap: 12px;
    padding: 22px 0;
    border-bottom: 1px solid var(--rule);
  }

  .sec h2 {
    margin: 0;
    font-size: var(--t-lg);
  }

  .you {
    display: flex;
    gap: 16px;
    align-items: flex-end;
    flex-wrap: wrap;
  }

  .you .field {
    flex: 0 1 280px;
  }

  .you .note {
    flex: 1 1 260px;
    margin: 0 0 10px;
  }

  .two {
    grid-template-columns: 1fr 1fr;
  }

  .inline {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
  }

  form.inline {
    align-items: flex-end;
  }

  .grow {
    flex: 1 1 220px;
  }

  .note {
    font-size: var(--t-sm);
    max-width: 70ch;
  }
  .warn-you {
    color: var(--owes);
    font-weight: 600;
  }

  .small {
    font-size: var(--t-xs);
  }

  .preview {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .chip {
    display: inline-flex;
    gap: 6px;
    align-items: center;
    padding: 3px 10px;
    border-radius: 999px;
    background: var(--wash);
    font-size: var(--t-sm);
    font-weight: 600;
  }

  .chip.skip {
    opacity: 0.55;
    text-decoration: line-through;
  }

  .why {
    font-size: var(--t-xs);
    font-weight: 500;
  }

  .tools {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: center;
  }

  .tools .input {
    flex: 1 1 240px;
    max-width: 380px;
  }

  .seg {
    display: inline-flex;
    gap: 2px;
    padding: 3px;
    border-radius: 10px;
    background: var(--wash);
  }

  .seg button {
    padding: 6px 12px;
    border: 0;
    border-radius: 8px;
    background: none;
    color: var(--ink-2);
    font: inherit;
    font-size: var(--t-sm);
    font-weight: 600;
    cursor: pointer;
  }

  .seg button[aria-pressed="true"] {
    background: var(--surface);
    color: var(--ink);
    box-shadow: 0 1px 2px rgb(0 0 0 / 0.12);
  }

  .roles {
    display: grid;
    border: 1px solid var(--rule);
    border-radius: var(--r-md);
    overflow: hidden;
  }

  .rrow {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 80px 110px;
    gap: 8px;
    align-items: center;
    padding: 9px 12px;
    border-top: 1px solid var(--rule);
  }

  .with-person .rrow {
    grid-template-columns: minmax(0, 1fr) 80px 110px 110px;
  }

  .own {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
  }

  .tiny {
    font-size: 11px;
  }

  .person-pick {
    max-width: 320px;
    margin: 0 0 10px;
  }

  .cats {
    margin-top: 14px;
  }

  .cats-h {
    margin: 0 0 6px;
  }

  .cat-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 0 12px;
    margin-bottom: 8px;
  }

  @media (max-width: 560px) {
    .with-person .rrow {
      grid-template-columns: minmax(0, 1fr) 56px 72px 72px;
    }
  }

  .rrow.head {
    border-top: 0;
    background: var(--wash);
    font-size: var(--t-xs);
    font-weight: 650;
    color: var(--ink-2);
  }

  .rrow > span:not(:first-child) {
    text-align: center;
  }

  .link-btn {
    padding: 0;
    border: 0;
    background: none;
    color: var(--ink);
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  .link-btn:hover {
    text-decoration: underline;
  }

  .prow,
  .srow {
    display: grid;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    background: var(--surface);
  }

  .prow {
    grid-template-columns: auto 1fr 140px auto;
  }

  .prow.archived {
    opacity: 0.6;
  }

  .srow {
    grid-template-columns: 1fr 150px 1fr auto;
  }

  .formats {
    margin: 0 0 14px;
    padding-left: 18px;
    display: grid;
    gap: 6px;
    font-size: var(--t-sm);
  }

  .plan {
    display: grid;
    gap: 10px;
    padding: 14px;
    border-radius: var(--r-md);
    background: var(--wash);
  }

  .plan ul {
    margin: 0;
    padding-left: 18px;
    font-size: var(--t-sm);
  }

  @media (max-width: 640px) {
    .two {
      grid-template-columns: 1fr;
    }

    .prow {
      grid-template-columns: auto 1fr auto;
    }

    .prow select {
      display: none;
    }

    .srow {
      grid-template-columns: 1fr auto;
    }
  }
</style>
